import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Grid,
  Fade
} from '@mui/material';
import { THEME_COLORS, TYPOGRAPHY, ANIMATIONS } from '../constants';

/**
 * Composant pour afficher la page des résultats de soumission
 */
const ResultsPage = ({ 
  submissionResults, 
  onNewInscription, 
  getTitleMessage, 
  getTitleColor, 
  getSubmissionStats 
}) => {
  const { successCount, errorCount } = getSubmissionStats();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${THEME_COLORS.background.main} 0%, ${THEME_COLORS.background.dark} 100%)`,
        py: { xs: 3, sm: 4, md: 6 },
        px: { xs: 2, sm: 3, md: 4 },
        position: 'relative'
      }}
    >
      <Box
        sx={{
          maxWidth: { xs: '100%', sm: 700, md: 900 },
          mx: 'auto',
          position: 'relative',
          zIndex: 1
        }}
      >
        {/* Titre moderne avec effet glassmorphism */}
        <Box
          sx={{
            textAlign: 'center',
            mb: { xs: 4, sm: 5, md: 6 },
            px: { xs: 1, sm: 2 }
          }}
        >
          <Typography 
            variant="h1"
            component="h1"
            sx={{ 
              fontSize: { 
                xs: TYPOGRAPHY.fontSize['2xl'], 
                sm: TYPOGRAPHY.fontSize['3xl'], 
                md: TYPOGRAPHY.fontSize['4xl'] 
              },
              fontWeight: TYPOGRAPHY.fontWeight.extrabold,
              fontFamily: TYPOGRAPHY.fontFamily.primary,
              color: getTitleColor(),
              textAlign: 'center',
              mb: { xs: 2, sm: 3 },
              letterSpacing: '-0.025em',
              lineHeight: 1.2
            }}
          >
            {getTitleMessage()}
          </Typography>
          
          <Typography 
            variant="h6"
            sx={{ 
              color: THEME_COLORS.text.secondary,
              fontFamily: TYPOGRAPHY.fontFamily.primary,
              fontWeight: TYPOGRAPHY.fontWeight.medium,
              fontSize: { xs: TYPOGRAPHY.fontSize.base, sm: TYPOGRAPHY.fontSize.lg },
              maxWidth: { xs: '100%', sm: 500 },
              mx: 'auto',
              lineHeight: 1.5
            }}
          >
            {successCount} participant{successCount > 1 ? 's' : ''} inscrit{successCount > 1 ? 's' : ''} avec succès
            {errorCount > 0 && `, ${errorCount} erreur${errorCount > 1 ? 's' : ''}`}
          </Typography>
        </Box>

        {/* Container principal moderne */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4, md: 5 },
            borderRadius: { xs: 3, sm: 4 },
            background: THEME_COLORS.background.glass,
            backdropFilter: 'blur(20px)',
            border: `1px solid ${THEME_COLORS.border.light}`,
            boxShadow: THEME_COLORS.shadow.xl,
            transition: ANIMATIONS.transition.normal
          }}
        >
          {/* Section header */}
          <Box sx={{ mb: { xs: 3, sm: 4 }, textAlign: 'center' }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontFamily: TYPOGRAPHY.fontFamily.primary,
                fontWeight: TYPOGRAPHY.fontWeight.semibold,
                color: THEME_COLORS.text.primary,
                fontSize: { xs: TYPOGRAPHY.fontSize.base, sm: TYPOGRAPHY.fontSize.lg },
                mb: { xs: 1, sm: 2 }
              }}
            >
              📊 Détails par participant
            </Typography>
          </Box>

          {/* Résultats détaillés avec grille responsive */}
          <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 4, sm: 5 } }}>
            {submissionResults.map((result, index) => (
              <Grid item xs={12} sm={6} lg={4} key={index}>
                <Fade in={true} timeout={300 + index * 100}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 2.5, sm: 3 },
                      borderRadius: { xs: 2, sm: 3 },
                      background: THEME_COLORS.background.paper,
                      border: `1px solid ${result.success ? THEME_COLORS.success.main : THEME_COLORS.error.main}20`,
                      position: 'relative',
                      transition: ANIMATIONS.transition.normal,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      '&:hover': {
                        transform: { xs: 'none', sm: 'translateY(-2px)' },
                        boxShadow: result.success 
                          ? `0 8px 24px ${THEME_COLORS.success.main}20`
                          : `0 8px 24px ${THEME_COLORS.error.main}20`,
                        borderColor: result.success 
                          ? `${THEME_COLORS.success.main}40`
                          : `${THEME_COLORS.error.main}40`
                      }
                    }}
                  >
                    {/* Header avec statut */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: { xs: 32, sm: 36 },
                            height: { xs: 32, sm: 36 },
                            borderRadius: { xs: 2, sm: 3 },
                            background: result.success 
                              ? THEME_COLORS.success.gradient 
                              : THEME_COLORS.error.gradient,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: result.success
                              ? `0 2px 8px ${THEME_COLORS.success.main}25`
                              : `0 2px 8px ${THEME_COLORS.error.main}25`
                          }}
                        >
                          <Typography
                            sx={{
                              color: 'white',
                              fontSize: { xs: '1rem', sm: '1.2rem' }
                            }}
                          >
                            {result.success ? '✅' : '❌'}
                          </Typography>
                        </Box>
                        <Chip 
                          label={result.participant.type}
                          icon={
                            <Typography sx={{ fontSize: { xs: '0.8rem', sm: '1rem' }, mr: 0.5 }}>
                              {result.participant.type === 'Accompagnateur' ? '👥' : '🪖'}
                            </Typography>
                          }
                          sx={{
                            background: result.participant.type === 'Accompagnateur' 
                              ? THEME_COLORS.primary.gradient 
                              : THEME_COLORS.secondary.gradient,
                            color: 'white',
                            fontWeight: TYPOGRAPHY.fontWeight.semibold,
                            fontFamily: TYPOGRAPHY.fontFamily.primary,
                            fontSize: { xs: TYPOGRAPHY.fontSize.xs, sm: TYPOGRAPHY.fontSize.sm },
                            borderRadius: { xs: 1.5, sm: 2 },
                            height: { xs: 24, sm: 28 },
                            '& .MuiChip-label': {
                              px: { xs: 0.5, sm: 1 }
                            }
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Contenu participant */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
                      {/* Nom */}
                      {result.participant.firstName && result.participant.lastName && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}>👤</Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: TYPOGRAPHY.fontWeight.semibold,
                              fontFamily: TYPOGRAPHY.fontFamily.primary,
                              color: THEME_COLORS.text.primary,
                              fontSize: { xs: TYPOGRAPHY.fontSize.base, sm: TYPOGRAPHY.fontSize.lg },
                              lineHeight: 1.3
                            }}
                          >
                            {result.participant.firstName} {result.participant.lastName}
                          </Typography>
                        </Box>
                      )}

                      {/* Email */}
                      {result.participant.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>📧</Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: TYPOGRAPHY.fontFamily.primary,
                              color: THEME_COLORS.text.secondary,
                              fontSize: { xs: TYPOGRAPHY.fontSize.sm, sm: TYPOGRAPHY.fontSize.sm },
                              wordBreak: 'break-word'
                            }}
                          >
                            {result.participant.email}
                          </Typography>
                        </Box>
                      )}

                      {/* Statut */}
                      <Box sx={{ mt: 'auto', pt: 1 }}>
                        <Chip
                          label={result.success ? '✅ Inscrit avec succès' : `❌ ${result.error}`}
                          size="small"
                          sx={{
                            backgroundColor: result.success 
                              ? THEME_COLORS.success.main + '15'
                              : THEME_COLORS.error.main + '15',
                            color: result.success 
                              ? THEME_COLORS.success.main
                              : THEME_COLORS.error.main,
                            fontFamily: TYPOGRAPHY.fontFamily.primary,
                            fontWeight: TYPOGRAPHY.fontWeight.medium,
                            fontSize: { xs: '0.6rem', sm: TYPOGRAPHY.fontSize.xs },
                            height: { xs: 20, sm: 24 },
                            width: '100%',
                            justifyContent: 'flex-start'
                          }}
                        />
                      </Box>
                    </Box>
                  </Paper>
                </Fade>
              </Grid>
            ))}
          </Grid>

          {/* Boutons d'action modernisés */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'center', 
            gap: { xs: 2, sm: 3 },
            mt: { xs: 3, sm: 4 }
          }}>
            <Button
              variant="outlined"
              onClick={onNewInscription}
              sx={{ 
                px: { xs: 4, sm: 6 },
                py: { xs: 1.5, sm: 2 },
                fontSize: { xs: TYPOGRAPHY.fontSize.base, sm: TYPOGRAPHY.fontSize.base },
                fontWeight: TYPOGRAPHY.fontWeight.medium,
                fontFamily: TYPOGRAPHY.fontFamily.primary,
                textTransform: 'none',
                borderRadius: { xs: 3, sm: 4 },
                borderColor: THEME_COLORS.primary.main,
                color: THEME_COLORS.primary.main,
                transition: ANIMATIONS.transition.normal,
                '&:hover': {
                  borderColor: THEME_COLORS.primary.light,
                  color: THEME_COLORS.primary.light,
                  backgroundColor: THEME_COLORS.primary.main + '08',
                  transform: { xs: 'none', sm: 'translateY(-1px)' }
                }
              }}
              startIcon={
                <Typography sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}>🔄</Typography>
              }
            >
              Nouvelle inscription
            </Button>
            
            <Button
              variant="contained"
              onClick={() => window.location.href = '/'}
              sx={{ 
                px: { xs: 4, sm: 6 },
                py: { xs: 1.5, sm: 2 },
                fontSize: { xs: TYPOGRAPHY.fontSize.base, sm: TYPOGRAPHY.fontSize.base },
                fontWeight: TYPOGRAPHY.fontWeight.semibold,
                fontFamily: TYPOGRAPHY.fontFamily.primary,
                textTransform: 'none',
                borderRadius: { xs: 3, sm: 4 },
                background: THEME_COLORS.success.gradient,
                color: 'white',
                border: 'none',
                boxShadow: `0 4px 16px ${THEME_COLORS.success.main}30`,
                transition: ANIMATIONS.transition.normal,
                '&:hover': {
                  transform: { xs: 'none', sm: 'translateY(-2px)' },
                  boxShadow: `0 6px 20px ${THEME_COLORS.success.main}40`
                }
              }}
              startIcon={
                <Typography sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}>🏠</Typography>
              }
            >
              Retour à l'accueil
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default ResultsPage;